import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
// import { API_BASE } from '../config';
import Captcha, { CaptchaHandle } from '../components/Captcha';
import {
  ShieldAlert,
  Phone,
  Package,
  KeyRound,
  MapPin,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Truck,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { useSeo, SITE_NAME } from '../utils/seo';

interface OrderInfo {
  valid: boolean;
  companyName: string;
  companyLogo?: string | null;
  orderNumber: string;
  maskedPhone: string;
}

interface AddressData {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  deliveryNotes?: string;
}

export default function AddressUpdatePortal() {
  // Customer-facing, but reached with a one-time ?token= in the URL: an
  // indexed copy would publish the token and the order behind it.
  useSeo({
    title: 'Update Your Delivery Address',
    description: 'Confirm or update the delivery address for your order.',
    sensitive: true,
  });

  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('token') || sessionStorage.getItem('address_update_token') || '';

  const [token, setToken] = useState(rawToken);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  const getApiBase = () => {
    const envApi = (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE || '').trim();
    if (envApi) {
      // Remove trailing versions or subpaths to get base, then add /v1
      return `${envApi.replace(/\/v1(\/admin|\/api)?.*$/, '')}/v1`;
    }
    return 'http://api.magento.local/v1';
  };


  // Order Info & Verification State
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [infoError, setInfoError] = useState('');

  // Step 1: Verification Form
  const [orderNumber, setOrderNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [step1CaptchaToken, setStep1CaptchaToken] = useState('');
  const [step1CaptchaKey, setStep1CaptchaKey] = useState(0);
  const step1CaptchaRef = useRef<CaptchaHandle>(null);
  const [requestingOtp, setRequestingOtp] = useState(false);
  const [step1Error, setStep1Error] = useState('');

  // Step 2: OTP Form
  const [otp, setOtp] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [step2Error, setStep2Error] = useState('');
  const [debugOtp, setDebugOtp] = useState<string | undefined>();
  const [resendTimer, setResendTimer] = useState(0);

  // Step 3: Address Form
  const [oldAddress, setOldAddress] = useState<any>(null);
  const [newAddress, setNewAddress] = useState<AddressData>({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    deliveryNotes: '',
  });
  const [step3CaptchaToken, setStep3CaptchaToken] = useState('');
  const [step3CaptchaKey, setStep3CaptchaKey] = useState(0);
  const step3CaptchaRef = useRef<CaptchaHandle>(null);
  const [submittingAddress, setSubmittingAddress] = useState(false);
  const [step3Error, setStep3Error] = useState('');

  // Save token to session storage if present in URL
  useEffect(() => {
    if (searchParams.get('token')) {
      const urlToken = searchParams.get('token')!;
      setToken(urlToken);
      sessionStorage.setItem('address_update_token', urlToken);
    }
  }, [searchParams]);

  // Load basic order info when token is available
  useEffect(() => {
    if (!token) {
      setLoadingInfo(false);
      setInfoError('Missing address update link token. Please check your link.');
      return;
    }

    setLoadingInfo(true);
    setInfoError('');

    fetch(`${getApiBase()}/api/public/address-update/info?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const json = await res.json().catch(() => null);
        if (!res.ok) throw new Error(json?.message || 'Invalid or expired link token');
        return json;
      })
      .then((data: OrderInfo) => {
        setOrderInfo(data);
        if (data.orderNumber) {
          setOrderNumber(data.orderNumber);
        }
      })
      .catch((err) => {
        setInfoError(err.message || 'We couldn\'t load your order. Please check the order number and try again.');
      })
      .finally(() => setLoadingInfo(false));
  }, [token]);

  // Handle OTP resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle Step 1: Request OTP
  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep1Error('');

    if (!orderNumber.trim()) {
      setStep1Error('Please enter your Order Number');
      return;
    }
    if (!phoneNumber.trim()) {
      setStep1Error('Please enter your Phone Number');
      return;
    }
    if (!step1CaptchaToken) {
      setStep1Error('Please complete the security check (captcha)');
      return;
    }

    const fullPhoneNumber = phoneNumber.trim().startsWith('+')
      ? phoneNumber.trim()
      : `${countryCode} ${phoneNumber.trim()}`;

    setRequestingOtp(true);
    try {
      const res = await fetch(`${getApiBase()}/api/public/address-update/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          orderNumber: orderNumber.trim(),
          phoneNumber: fullPhoneNumber,
          captchaToken: step1CaptchaToken,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        step1CaptchaRef.current?.reset();
        setStep1CaptchaToken('');
        setStep1CaptchaKey((k) => k + 1);
        throw new Error(data.message || 'We couldn\'t send your verification code. Check the phone number and try again.');
      }

      if (data.debugOtp) {
        setDebugOtp(data.debugOtp);
      }

      setResendTimer(60);
      setStep(2);
    } catch (err: any) {
      step1CaptchaRef.current?.reset();
      setStep1CaptchaToken('');
      setStep1CaptchaKey((k) => k + 1);
      setStep1Error(err.message || 'We couldn\'t process that request. Please try again in a moment.');
    } finally {
      setRequestingOtp(false);
    }
  };

  // Handle Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep2Error('');

    if (!otp.trim() || otp.trim().length !== 6) {
      setStep2Error('Please enter the full 6-digit verification code');
      return;
    }

    setVerifyingOtp(true);
    try {
      const res = await fetch(`${getApiBase()}/api/public/address-update/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          otp: otp.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Invalid verification code');
      }

      if (data.oldAddress) {
        setOldAddress(data.oldAddress);
        setNewAddress({
          street1: data.oldAddress.street1 || data.oldAddress.street || '',
          street2: data.oldAddress.street2 || '',
          city: data.oldAddress.city || '',
          state: data.oldAddress.state || '',
          zip: data.oldAddress.zip || data.oldAddress.postalCode || '',
          country: data.oldAddress.country || 'US',
          deliveryNotes: '',
        });
      }

      setStep(3);
    } catch (err: any) {
      setStep2Error(err.message || 'Verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Handle Step 3: Submit Updated Address
  const handleSubmitAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep3Error('');

    if (!newAddress.street1.trim()) {
      setStep3Error('Street Address 1 is required');
      return;
    }
    if (!newAddress.city.trim()) {
      setStep3Error('City is required');
      return;
    }
    if (!newAddress.state.trim()) {
      setStep3Error('State / Province is required');
      return;
    }
    if (!newAddress.zip.trim()) {
      setStep3Error('Postal / Zip Code is required');
      return;
    }
    if (!step3CaptchaToken) {
      setStep3Error('Please complete the security check (captcha)');
      return;
    }

    setSubmittingAddress(true);
    try {
      const res = await fetch(`${getApiBase()}/api/public/address-update/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          newAddress,
          captchaToken: step3CaptchaToken,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        step3CaptchaRef.current?.reset();
        setStep3CaptchaToken('');
        setStep3CaptchaKey((k) => k + 1);
        throw new Error(data.message || 'We couldn\'t update your address. Nothing was changed — please try again.');
      }

      setStep(4);
    } catch (err: any) {
      step3CaptchaRef.current?.reset();
      setStep3CaptchaToken('');
      setStep3CaptchaKey((k) => k + 1);
      setStep3Error(err.message || 'We couldn\'t update your address. Nothing was changed — please try again.');
    } finally {
      setSubmittingAddress(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mb-4"></div>
        <p className="text-slate-400 font-medium animate-pulse">Loading order details...</p>
      </div>
    );
  }

  if (infoError || !token) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900/90 border border-slate-800 rounded-2xl p-8 text-center shadow-2xl backdrop-blur-xl">
          <div className="w-16 h-16 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-rose-400">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {infoError.includes('already been used') || infoError.includes('expired') ? 'Link Expired' : 'Invalid Link'}
          </h2>
          <p className="text-slate-400 text-sm mb-6">{infoError || 'This address update link is invalid or expired.'}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm transition-all"
          >
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col selection:bg-indigo-500 selection:text-white relative overflow-hidden">
      {/* Dynamic Background Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-gradient-to-b from-indigo-600/10 via-purple-600/5 to-transparent blur-3xl pointer-events-none" />

      {/* Top Header Navigation */}
      <header className="border-b border-slate-800/80 bg-slate-900/40 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-base text-white tracking-tight leading-tight">
                {orderInfo?.companyName || 'Shipping Portal'}
              </h1>
              <p className="text-xs text-slate-400">Address Correction Portal</p>
            </div>
          </div>
          {orderInfo?.orderNumber && (
            <div className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700 text-indigo-300">
              <Package className="w-3.5 h-3.5" />
              <span>Order #{orderInfo.orderNumber}</span>
            </div>
          )}
        </div>
      </header>

      {/* Main Content Container */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8 relative z-10">
        {/* Step Progression Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-md mx-auto mb-2 px-2">
            <div className={`flex flex-col items-center ${step >= 1 ? 'text-indigo-400 font-semibold' : 'text-slate-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border ${step >= 1 ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300' : 'border-slate-700 bg-slate-900'}`}>
                1
              </div>
              <span className="text-[11px] mt-1">Verify</span>
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${step >= 2 ? 'bg-indigo-500' : 'bg-slate-800'}`} />
            <div className={`flex flex-col items-center ${step >= 2 ? 'text-indigo-400 font-semibold' : 'text-slate-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border ${step >= 2 ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300' : 'border-slate-700 bg-slate-900'}`}>
                2
              </div>
              <span className="text-[11px] mt-1">OTP</span>
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${step >= 3 ? 'bg-indigo-500' : 'bg-slate-800'}`} />
            <div className={`flex flex-col items-center ${step >= 3 ? 'text-indigo-400 font-semibold' : 'text-slate-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border ${step >= 3 ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300' : 'border-slate-700 bg-slate-900'}`}>
                3
              </div>
              <span className="text-[11px] mt-1">Update</span>
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${step === 4 ? 'bg-emerald-500' : 'bg-slate-800'}`} />
            <div className={`flex flex-col items-center ${step === 4 ? 'text-emerald-400 font-semibold' : 'text-slate-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border ${step === 4 ? 'bg-emerald-600/20 border-emerald-500 text-emerald-300' : 'border-slate-700 bg-slate-900'}`}>
                4
              </div>
              <span className="text-[11px] mt-1">Done</span>
            </div>
          </div>
        </div>

        {/* ================= STEP 1: VERIFICATION FORM ================= */}
        {step === 1 && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold mb-3">
                <ShieldAlert className="w-3.5 h-3.5" />
                <span>Address Verification Required</span>
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Confirm Your Order Details</h2>
              <p className="text-slate-400 text-sm mt-1">
                Your order shipping address needs correction. Please verify your order number and phone number to proceed.
              </p>
            </div>

            {step1Error && (
              <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm flex items-start gap-3">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div>{step1Error}</div>
              </div>
            )}

            <form onSubmit={handleRequestOtp} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                  Order Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Package className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    placeholder="e.g. 10042"
                    className="w-full pl-11 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-mono text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-500 mb-2">Must match phone on order ({orderInfo?.maskedPhone})</p>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="px-3 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 font-mono text-sm shrink-0 cursor-pointer"
                  >
                    <option value="+1">🇺🇸 +1 (US/CA)</option>
                    <option value="+44">🇬🇧 +44 (UK)</option>
                    <option value="+91">🇮🇳 +91 (IN)</option>
                    <option value="+61">🇦🇺 +61 (AU)</option>
                    <option value="+49">🇩🇪 +49 (DE)</option>
                    <option value="+33">🇫🇷 +33 (FR)</option>
                    <option value="+81">🇯🇵 +81 (JP)</option>
                    <option value="+86">🇨🇳 +86 (CN)</option>
                    <option value="+55">🇧🇷 +55 (BR)</option>
                    <option value="+52">🇲🇽 +52 (MX)</option>
                  </select>
                  <div className="relative flex-1">
                    <Phone className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="e.g. (555) 019-2834"
                      className="w-full pl-11 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-mono text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Captcha Component */}
              <Captcha ref={step1CaptchaRef} resetTrigger={step1CaptchaKey} onVerify={(token) => setStep1CaptchaToken(token)} />

              <button
                type="submit"
                disabled={requestingOtp}
                className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold text-sm shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              >
                {requestingOtp ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Sending Code...</span>
                  </>
                ) : (
                  <>
                    <span>Send SMS Verification Code</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* ================= STEP 2: OTP VERIFICATION ================= */}
        {step === 2 && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-semibold mb-3">
                <KeyRound className="w-3.5 h-3.5" />
                <span>Security Verification</span>
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Enter 6-Digit Code</h2>
              <p className="text-slate-400 text-sm mt-1">
                We sent a 6-digit SMS verification code to your phone number ending in <span className="font-semibold text-slate-200">{phoneNumber.slice(-4)}</span>.
              </p>
            </div>

            {debugOtp && (
              <div className="mb-4 p-3 rounded-xl bg-indigo-950/60 border border-indigo-500/40 text-indigo-300 text-xs flex items-center justify-between">
                <span>[Dev Mode Debug OTP]: <strong className="text-white text-sm font-mono tracking-widest">{debugOtp}</strong></span>
              </div>
            )}

            {step2Error && (
              <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm flex items-start gap-3">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div>{step2Error}</div>
              </div>
            )}

            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                  Verification Code (OTP) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full text-center py-3.5 px-4 bg-slate-950/80 border border-slate-800 rounded-xl text-2xl font-mono tracking-[0.5em] text-indigo-300 placeholder-slate-700 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                  required
                  autoFocus
                />
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>Didn't receive the code?</span>
                <button
                  type="button"
                  disabled={resendTimer > 0}
                  onClick={(e) => handleRequestOtp(e as any)}
                  className="text-indigo-400 hover:text-indigo-300 font-semibold disabled:text-slate-600 disabled:no-underline underline transition-colors"
                >
                  {resendTimer > 0 ? `Resend Code in ${resendTimer}s` : 'Resend Code'}
                </button>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-5 py-3 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 font-medium text-sm transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={verifyingOtp || otp.length !== 6}
                  className="flex-1 py-3.5 px-6 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold text-sm shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                >
                  {verifyingOtp ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify & Continue</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ================= STEP 3: ADDRESS CORRECTION FORM ================= */}
        {step === 3 && (
          <div className="space-y-6">
            {/* Old Address Card */}
            {oldAddress && (
              <div className="bg-amber-950/20 border border-amber-500/30 rounded-2xl p-5 backdrop-blur-md">
                <div className="flex items-center gap-2 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">
                  <RotateCcw className="w-4 h-4" />
                  <span>Current (Flagged) Address on File</span>
                </div>
                <div className="text-slate-300 text-sm space-y-0.5 font-medium pl-6 border-l-2 border-amber-500/40">
                  <p>{oldAddress.street1 || oldAddress.street || 'N/A'}</p>
                  {oldAddress.street2 && <p>{oldAddress.street2}</p>}
                  <p>
                    {oldAddress.city || 'N/A'}, {oldAddress.state || ''} {oldAddress.zip || oldAddress.postalCode || ''}
                  </p>
                  <p className="text-slate-400 text-xs">{oldAddress.country || 'US'}</p>
                </div>
              </div>
            )}

            {/* New Address Form */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold mb-3">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>Correct Delivery Address</span>
                </div>
                <h2 className="text-2xl font-bold text-white tracking-tight">Enter Corrected Address</h2>
                <p className="text-slate-400 text-sm mt-1">
                  Please provide the complete, updated shipping address for your order.
                </p>
              </div>

              {step3Error && (
                <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>{step3Error}</div>
                </div>
              )}

              <form onSubmit={handleSubmitAddress} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Street Address 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newAddress.street1}
                    onChange={(e) => setNewAddress({ ...newAddress, street1: e.target.value })}
                    placeholder="123 Main Street"
                    className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Street Address 2 / Suite / Apt (Optional)
                  </label>
                  <input
                    type="text"
                    value={newAddress.street2}
                    onChange={(e) => setNewAddress({ ...newAddress, street2: e.target.value })}
                    placeholder="Apt 4B / Suite 200"
                    className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newAddress.city}
                      onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                      placeholder="New York"
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                      State / Province <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newAddress.state}
                      onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                      placeholder="NY"
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                      Postal / Zip Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newAddress.zip}
                      onChange={(e) => setNewAddress({ ...newAddress, zip: e.target.value })}
                      placeholder="10001"
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Country *
                  </label>
                  <select
                    value={newAddress.country}
                    onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm"
                  >
                    <option value="US">United States (US)</option>
                    <option value="CA">Canada (CA)</option>
                    <option value="GB">United Kingdom (GB)</option>
                    <option value="AU">Australia (AU)</option>
                    <option value="DE">Germany (DE)</option>
                    <option value="FR">France (FR)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Delivery Notes / Special Instructions (Optional)
                  </label>
                  <textarea
                    rows={2}
                    value={newAddress.deliveryNotes}
                    onChange={(e) => setNewAddress({ ...newAddress, deliveryNotes: e.target.value })}
                    placeholder="e.g. Leave package at front gate"
                    className="w-full px-4 py-2.5 bg-slate-950/80 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm resize-none"
                  />
                </div>

                {/* Captcha Protection */}
                <Captcha ref={step3CaptchaRef} resetTrigger={step3CaptchaKey} onVerify={(token) => setStep3CaptchaToken(token)} />

                <button
                  type="submit"
                  disabled={submittingAddress}
                  className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold text-sm shadow-lg shadow-emerald-600/25 flex items-center justify-center gap-2 transition-all disabled:opacity-50 mt-4"
                >
                  {submittingAddress ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Updating Address...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Save & Update Order Address</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ================= STEP 4: SUCCESS CONFIRMATION ================= */}
        {step === 4 && (
          <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-8 text-center shadow-2xl backdrop-blur-xl">
            <div className="w-20 h-20 bg-emerald-500/10 border border-emerald-500/30 rounded-3xl flex items-center justify-center mx-auto mb-6 text-emerald-400 shadow-xl shadow-emerald-500/10 animate-bounce">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <h2 className="text-3xl font-extrabold text-white tracking-tight mb-2">Address Updated Successfully!</h2>
            <p className="text-slate-400 text-sm max-w-md mx-auto mb-8">
              Thank you! Your order shipping destination has been updated. Our logistics team will re-validate the label and resume shipment processing.
            </p>

            <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-6 text-left max-w-md mx-auto mb-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-3">
                <Sparkles className="w-4 h-4" />
                <span>Updated Address Summary</span>
              </div>
              <div className="text-slate-200 text-sm space-y-1 font-medium">
                <p>{newAddress.street1}</p>
                {newAddress.street2 && <p>{newAddress.street2}</p>}
                <p>{newAddress.city}, {newAddress.state} {newAddress.zip}</p>
                <p className="text-slate-400 text-xs">{newAddress.country}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-medium text-sm transition-all"
              >
                Return to Homepage
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Simple Footer */}
      <footer className="border-t border-slate-800/80 py-6 text-center text-xs text-slate-500">
        <p>© {new Date().getFullYear()} {orderInfo?.companyName || SITE_NAME}. All rights reserved.</p>
      </footer>
    </div>
  );
}
